import React, { useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import useCostapassState from 'hooks/useCostapassState';
import { getSessionId } from 'utils/session';
import { CurrentLang } from 'utils/lang';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';

/**
 * Clean the brand name by removing all - and _ and converting to lowercase
 * @param {string} brand - The brand name to clean
 * @returns {string} - The cleaned brand name
 */
const cleanBrand = (brand) => {
  return brand.replace(/[-_]/g, '').toLowerCase();
};

/**
 * TpFundWidget component. Loads the TP Fund Widget from the specified URL and configures it with required variables.
 * @returns {JSX} - React component
 */
const TpFundWidget = () => {
  const widgetSelector = useRef(uuidv4());
  const costapassState = useCostapassState();
  const sessionId = getSessionId();
  const {
    tpFundWidget: { version, isProduction, baseUrl },
  } = useWhitelabelEnvs();

  const { brand, token } = costapassState?.profile || {};

  const profileToken = token;
  const lang = CurrentLang();

  useEffect(() => {
    // Create script element for the widget
    const script = document.createElement('script');
    script.src = `https://widgets.reservamos.mx/tp-fund/${version}/widget.js`;
    script.setAttribute('data-target-id', widgetSelector.current);

    // Handle script loading events
    script.onload = () => {
      if (window?.TPFundWidget?.isInitialized) {
        console.log('TP Fund Widget script is initialized');
        window.TPFundWidget.onDepositSuccess(() => {
          // Force refresh of the CostaPass profile widget
          if (window.reservamosPubSub && window.reservamosPubSub.pub) {
            window.reservamosPubSub.pub('costapass_profile_refresh', {
              force: true,
            });
            console.log('Triggered CostaPass profile refresh after successful deposit');
          }
        });
      } else {
        console.log('TP Fund Widget script is not loaded properly');
      }
    };

    // Add script to document
    document.body.appendChild(script);

    // Clean up on component unmount
    return () => {
      if (window?.TPFundWidget?.isInitialized) {
        console.log('Destroying TP Fund Widget');
        window.TPFundWidget.destroy();
      }
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dataBrand = cleanBrand(brand || '');
  return (
    <div
      id={widgetSelector.current}
      data-token={profileToken}
      data-session-id={sessionId}
      data-brand={dataBrand}
      data-locale={lang}
      data-is-production={isProduction}
      data-base-url={baseUrl}
      data-enable-three-ds="true"
    />
  );
};

export default TpFundWidget;
